import { Component, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, AlertTriangle, Banknote, TrendingUp, Landmark } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* 🔑 Il saldo del conto NON è la somma dei movimenti in archivio. La serie parte dal
   02/01/2019, ma il conto quel giorno non era a zero: sommando solo i movimenti si
   arriva a −37.504,89 al 21/09/2026, mentre l'estratto conto di quel giorno dice
   7.014,81. La differenza è il saldo che il conto aveva già all'inizio della serie.

   I 44.519,70 non sono una stima: si ricavano per sottrazione da DUE saldi stampati
   sugli estratti conto — 5.927,08 al 30/06/2026 (saldo iniziale della stampa del
   21/09, quindi PRIMA dei movimenti di quel giorno) e 7.014,81 al 21/09/2026 (saldo
   contabile) — e i due calcoli danno lo stesso numero. Che coincidano prova anche
   che fra quelle due date la serie è completa: nessun movimento manca, nessuno è
   doppio. La sonda che lo rifà è `_saldo_conto.py`.

   Se un giorno si caricassero movimenti anteriori al 02/01/2019, questo numero va
   rifatto con la stessa sonda: è l'apertura DELLA SERIE, non una costante del conto. */
const APERTURA_SERIE = 44519.70;
const APERTURA_DA = '2019-01-02';

/*
  Cruscotto — grafici per anno e criticita' riscontrate.

  TUTTO QUELLO CHE SI PUO' CALCOLARE, SI CALCOLA. Non c'e' nessuna immagine e nessun
  numero copiato: a ogni apertura la pagina interroga l'API e ridisegna. Se domani si
  registra un incasso, il grafico cambia da solo.

  Le fonti sono gli endpoint che gia' esistono — `quote-periods`, le quote di ogni
  periodo, `bank-transactions` — aggregati nel browser. **Nessuna rotta nuova sul
  backend**, quindi l'immagine del backend non si ricostruisce.

  Anche le criticita' che stanno in banca si ricavano dai movimenti, ogni volta:
  i compensi 770 e le consulenze del lavoro, le parcelle dell'amministrazione.
  Cambiano da sole quando si importa un nuovo estratto conto.

  🔴 Fanno eccezione sei voci, ed e' un'eccezione dichiarata: sono somme che **non
  hanno mai attraversato il conto corrente** e che quindi in banca non si possono
  trovare. Vengono da documenti cartacei — il libro di portineria, il foglio dei
  contanti trattenuti, il registro di contabilita' della precedente amministrazione.
  Stanno in FUORI_BANCA qui sotto, con la fonte accanto a ciascuna. Fingere che
  fossero calcolate sarebbe peggio che dichiararle.
*/

/* Ogni chiave disegnata deve stare qui dentro. Il fallback `|| '#888'` nel grafico non
   è una rete di sicurezza: è il modo in cui due serie diverse escono dello stesso
   grigio, con la legenda che mostra due quadratini identici e il lettore che non ha
   modo di sapere quale barra è quale. Una chiave nuova senza il suo colore non rompe
   niente e rende il grafico illeggibile. */
const COLORI = {
  contanti: '#8B1E2D', pos: '#0D4D4F', bonifico: '#3E7C7E', altro: '#9C8F7A',
  emesso: '#C9BFAE', incassato: '#0D4D4F', residuo: '#8B1E2D',
  entrate: '#0D4D4F', uscite: '#8B1E2D',
  spese: '#8B1E2D', pagate: '#3E7C7E'
};

/* La polizza Catalano costa DODICI rate da 105,00 l'anno: 1.260,00 di costo annuo per
   il condominio. Gli assegni che si leggono in banca sono solo la parte che l'estratto
   conto mostra; le rate che in assegno non compaiono sono state pagate per bonifico o
   in contanti nello stesso anno, e vanno cercate lì — non sono rate non pagate.

   Perciò questa scheda NON si calcola più dai movimenti: contare gli addebiti fra
   104,00 e 105,50 dava il numero degli assegni, non il costo della polizza. I numeri
   qui sotto sono gli stessi di `_criticita_per_anno.py` (RATA_POLIZZA, POLIZZA_ANNI),
   ricostruiti con il filtro stretto su «ADDEBITO ASSEGNO» e Unipol: se cambiano lì,
   vanno cambiati anche qui. Per anno: [rate rintracciate in banca, loro importo,
   rate dovute] — nel 2025 solo gennaio-luglio, il riscatto è di fine luglio. */
const RATA_POLIZZA = 105.00;
const POLIZZA_ANNI = {
  2019: [16, 1678.16, 12], 2020: [12, 1259.05, 12], 2021: [11, 1152.95, 12],
  2022: [12, 1257.96, 12], 2023: [9, 945.35, 12], 2024: [13, 1365.53, 12],
  2025: [4, 420.00, 7]
};

const RE_770 = /\b770\b|certificazione unica|consulen\w*\s+(del\s+)?lavoro|elaborazione paghe|cascio renato/i;
const RE_PARCELLA = /parcell|onorar/i;

// Somme documentate che in banca non ci sono: non si possono calcolare, si dichiarano.
const FUORI_BANCA = [
  { anno: '2025', tipo: 'polizza', importo: 10728.36,
    titolo: 'Riscatto della polizza registrato e mai transitato in banca',
    nota: 'Registro di contabilità 2025 dello Studio Graviano. Sul conto corrente non '
        + 'risulta né l’accredito del riscatto né il bonifico al dipendente: sono '
        + 'scritture contabili, non movimenti bancari.' },
  { anno: '2025', tipo: 'polizza', importo: 10819.08, non_sommare: true,
    titolo: 'Polizza Catalano riscattata a metà del maturato: 21.350,87 maturati, 10.531,79 riscattati',
    nota: 'Documento Unico di Rendicontazione annuale Unipol del 12/04/2025 (contratto '
        + '589/100778864, tariffa PF31FB2V2, contraente CATALANO SALVATORE, decorrenza '
        + '15/03/2012, scadenza 15/03/2032). Al 15/03/2025 la prestazione maturata '
        + 'rivalutata era 21.350,87 e il valore di riscatto lordo 10.531,79: riscattando '
        + 'intorno al 25 luglio 2025 si sono persi 10.819,08, cioè poco più della metà '
        + 'del maturato, rispetto a portare il contratto a scadenza. Il cumulo dei premi '
        + 'pagati al 15/03/2024 era 14.815,00, quindi il riscatto ha restituito meno di '
        + 'quanto versato. Rendimento della gestione separata nel 2025: 2,84% lordo, meno '
        + 'l’1,00% di commissione di gestione e con il 2,00% di tasso tecnico già '
        + 'conteggiato, quindi rivalutazione effettiva zero. È un mancato guadagno: resta '
        + 'fra le criticità ma non entra nel totale delle somme fuori dal conto.' },
  { anno: '2025', tipo: 'ferie', importo: 2025.05,
    titolo: 'Ferie 2025 del portiere pagate in contanti dalla cassa',
    nota: 'Registro di contabilità 2025: 794,90 del 28/02 + 892,35 del 13/03 + 337,80 '
        + 'del 09/04. Il totale coincide con «Utilizzo fondi ferie» del conto '
        + 'economico, ma la voce è intestata «Banca c/c» mentre il denaro è uscito dal '
        + 'contante.' },
  { anno: '2026', tipo: 'contanti_non_versati', importo: 10108.39,
    titolo: 'Quote incassate in contanti e mai versate al condominio',
    nota: 'Foglio manoscritto delle ricevute per contanti, 14 righe; stesso totale in '
        + 'calce alla ricevuta 4/2026 del libro di portineria. Le quote risultano '
        + 'saldate perché i condòmini hanno pagato: la somma è un credito del '
        + 'Condominio verso il portiere.' },
  { anno: '2026', tipo: 'ferie', importo: 2500.00,
    titolo: 'Ferie 2026 auto-assegnate dal portiere sulle quote incassate',
    nota: 'Libro di portineria, ricevuta 4/2026: trattenute dai 10.108,39 di quote '
        + 'incassate in contanti, senza delibera assembleare.' },
  { anno: '2026', tipo: 'tfr', importo: 7608.39,
    titolo: 'Anticipo TFR auto-assegnato sulle quote incassate',
    nota: 'Libro di portineria, ricevuta 4/2026: 10.108,39 meno 2.500,00 di ferie = '
        + '7.608,39 trattenuti come anticipo del trattamento di fine rapporto, senza '
        + 'delibera assembleare.' },
  { anno: '2024', tipo: 'contanti_non_versati', importo: 19724.23,
    titolo: 'Incassi e pagamenti 2024 passati per la cassa contanti, fuori dal conto',
    nota: 'Riepilogo finanziario del rendiconto 2024: la cassa contanti dichiara '
        + '20.501,52 di entrate e 19.724,23 di uscite, contro un saldo iniziale di 227,87 '
        + 'e uno finale di 1.005,16. Gli incassi dichiarati nei flussi di cassa sommano '
        + '59.991,29 mentre in banca ne sono entrati 44.938,86: 15.052,43 che il conto '
        + 'corrente non ha mai visto. L’importo indicato è l’uscita, cioè il denaro speso '
        + 'senza passare dal conto. Stesso meccanismo dei contanti trattenuti dal '
        + 'portiere, ma per un anno intero e dichiarato dall’amministrazione stessa.' },
];

const ETICHETTE = {
  polizza_rate: 'Polizza Catalano', polizza: 'Polizza — riscatto',
  ferie: 'Ferie del portiere', tfr: 'Anticipo TFR',
  contanti_non_versati: 'Contanti non versati',
  settecentosettanta: '770 / consulenza lavoro', parcella: 'Parcella amministrazione',
  bilancio: 'Bilancio — discrepanza documentale',
  esercizio_mancante: 'Esercizio non ricostruito'
};

// Contraddizioni fra i documenti di bilancio (riparto 2023, elenco insolventi,
// preventivo 2024) e la piattaforma. Non sono somme fuori banca e non si sommano:
// ogni scheda e' un fatto documentale con la sua fonte. Chieste dalla titolare il
// 21/09/2026. Dettaglio: RIBASAMENTO-2023-PROVA-A-SECCO-2026-09-21.md.
const DOCUMENTALI = [
  { anno: '2023', tipo: 'bilancio', importo: 21729.17,
    titolo: 'Consuntivo 2023 mai ripartito in rate: gestione senza conguaglio emesso',
    nota: 'Riparto 2023 (pp. 4-5, 50 posizioni): totale gestione 65.719,25. Rate 2023 '
        + 'ricostruite da pagamenti ed elenco insolventi: 43.990,08. Nel fascicolo non '
        + 'esiste un preventivo 2023: la differenza non è mai stata chiesta ai condòmini '
        + 'come conguaglio.' },
  { anno: '2023', tipo: 'bilancio', importo: 2352.60,
    titolo: 'Elenco insolventi e riparto approvato in contraddizione (A4 Di Ganci, B4, B6)',
    nota: 'L’elenco insolventi (scadenze fino al 30/11/2024) dà rate 2023 non pagate per '
        + 'Di Ganci A4 1.433,76, B4 656,76 e B6 262,08; il riparto 2023 addebita alle '
        + 'stesse posizioni 78,39, 0,00 e 0,00.' },
  { anno: '2023', tipo: 'bilancio', importo: 12277.84,
    titolo: 'Riparto 2023 senza i saldi degli esercizi precedenti: «crediti» che sono arretrati pagati',
    nota: 'La colonna «Saldi di fine es. prec.» è a zero su tutte le 50 righe. Chi nel 2023 '
        + 'ha pagato arretrati risulta in credito nel saldo finale (S.A.M.O.T. A11 4.552,86 '
        + 'su un dovuto di 78,50, A13 2.363,08, Eredi Miceli 1.547,31, Cacioppo Giovanna '
        + '619,48). Somma dei saldi finali positivi: 12.277,84.' },
  { anno: '2023', tipo: 'bilancio', importo: 6948.49,
    titolo: 'Costi 2023 addebitati a conduttore e usufruttuario, pagamenti registrati sul proprietario',
    nota: 'Il riparto addebita a Tuttoilmondo Gaetano (A25) 3.786,55 e a Cacioppo Francesco '
        + '(A23) 3.161,94; le rate versate stanno su Eredi Miceli (1.767,47) e Cacioppo '
        + 'Giovanna (807,53), che risultano in credito. Verso il condominio risponde '
        + 'comunque il proprietario (art. 63 e 67 disp. att. c.c.).' },
  { anno: '2023', tipo: 'bilancio', importo: 6636.60,
    titolo: 'Acqua versata sommata nel saldo finale, acqua dovuta assente dal riparto',
    nota: 'La colonna «Saldo es. Gestione Acqua 2023» è l’acqua versata ed entra nel saldo '
        + 'finale come credito, ma il dovuto acqua 2023 non compare in nessuna colonna. Da '
        + 'chiarire con l’amministratore uscente.' },
  { anno: '2023', tipo: 'bilancio', importo: 449.27,
    titolo: 'Tre posizioni del riparto 2023 assenti nella piattaforma',
    nota: 'Amato Ettore A9 96,32, Eredi Troia A14 122,13, Monti Daniela B5 230,82: nel '
        + 'riparto, ma senza pagamenti né insoluti nelle altre fonti, quindi mai emesse.' },
  { anno: '2023', tipo: 'bilancio', importo: 807.48,
    titolo: 'Rata 2023 di Cacioppo sottostimata nelle fonti (135,50 invece di 202,79), rettificata',
    nota: 'Foglio incassi ed elenco insolventi erano entrambi incompleti sulla stessa '
        + 'posizione; le ricevute dell’amministrazione dicono 277,79 al mese. Rettificata '
        + 'il 21/09/2026.' },
  { anno: '2025', tipo: 'bilancio', importo: 11836.31,
    titolo: 'F24 2025 del portiere: 11.836,31 registrati come pagati, mai usciti dal conto',
    nota: 'Dodici F24 del 2025 risultano PAGATI in piattaforma per 11.858,31. Lo stato '
        + 'viene dal Rendiconto dell’esercizio 2025 dell’amministrazione uscente (CONTO '
        + 'ECONOMICO 2025_SIT. PATRIMONIALE E DI CASSA.pdf, bilancio dettagliato per '
        + 'conto, pagg. 15-22, protocolli G9, G16, G26 e seguenti), non da un movimento '
        + 'bancario. Cercando in banca un’uscita dello stesso importo al centesimo, in '
        + 'qualunque punto dell’archivio (896 uscite, dal 02/01/2019 al 21/09/2026), se '
        + 'ne trova una sola: i 22,00 del 12/09/2025. Gli altri undici, per 11.836,31, '
        + 'non hanno riscontro da nessuna parte. Il conto conferma il quadro: all’erario '
        + 'ha pagato 17.059,56 nel 2019, 12.014,65 nel 2020, 14.743,99 nel 2021, '
        + '10.488,99 nel 2022, poi 3.426,94 nel 2023, 359,59 nel 2024, 689,41 nel 2025 e '
        + '102,25 nel 2026. I versamenti si sono fermati dal 2023, mentre le buste paga '
        + 'hanno continuato a essere pagate. È il rovescio dei 38.708,41 di cartelle e '
        + 'avvisi, quasi tutti INPS per contributi del portiere.' },
  { anno: '2026', tipo: 'bilancio', importo: 6436.97,
    titolo: 'F24 2026 del portiere: sei deleghe per 6.436,97, nessuna pagata',
    nota: 'Sei F24 di competenza 2026 (gennaio, febbraio, marzo, maggio, giugno e '
        + 'luglio) sono registrati come da pagare per 6.436,97: 994,63 + 1.026,86 + '
        + '1.036,47 + 1.040,38 + 1.338,74 + 999,89. Controllato che non siano stati '
        + 'pagati e la registrazione sia rimasta indietro: in banca non esiste nessuna '
        + 'uscita di quegli importi. In tutto il 2026 il conto ha pagato all’erario '
        + '102,25, il 28/07, ed è un bollettino dell’Agenzia delle Entrate-Riscossione, '
        + 'cioè una cartella, non una delega corrente. Manca inoltre l’F24 di aprile '
        + '2026, che non è in cartella.' },
  { anno: '2025', tipo: 'bilancio', importo: 159.20,
    titolo: 'Ritenute d’acconto certificate e non versate: 159,20 su 781,20',
    nota: 'La Certificazione Unica 2026 (anno 2025), presentata all’Agenzia delle '
        + 'Entrate il 29/04/2026 con protocollo 26042920043934243, certifica quattro '
        + 'percipienti di redditi da lavoro autonomo e 781,20 di ritenute operate: '
        + 'Cascio Renato 156,00, IMET Impianti 3,20, Rambone Ciro 22,00, Graviano '
        + 'Pierina 600,00. I compensi sono usciti tutti dal conto e quadrano al '
        + 'centesimo, commissione di 1,19 compresa (796,79 il 16/01, 85,99 il 03/02, '
        + '584,19 il 05/08, 3.207,59 il 15/10 del 2025). Delle ritenute invece risultano '
        + 'versate solo due: 22,00 il 12/09/2025 e 600,00 il 15/10/2025, cioè 622,00. '
        + 'Mancano i 156,00 di Cascio (scadenza 16/02/2025) e i 3,20 di IMET (scadenza '
        + '16/03/2025): nell’intero 2025 il conto non ha pagato nessuna delega di quegli '
        + 'importi. Il condominio ha quindi certificato allo Stato ritenute che non ha '
        + 'versato.' },
  { anno: '2026', tipo: 'bilancio', importo: 2082.20,
    titolo: 'Amato Ettore: 2.082,20 versati senza che un documento dica quali mesi pagano',
    nota: 'Tre accrediti da Amato Ettore sulle posizioni A8 e A9 (intestate allo Studio '
        + 'legale Fucarino - Russo - Di Baudo) restano senza imputazione perché nessuno '
        + 'di essi individua le quote al centesimo. 1.000,00 del 27/10/2025, causale '
        + '«Pag. in acconto quote condominiali»: acconto, nessun mese dichiarato. '
        + '1.000,00 del 15/05/2026, causale «Pag. secondo acconto quote condominiali '
        + 'pregresse»: ancora un acconto, nessun mese. 82,20 dell’11/09/2026, causale '
        + '«Pag. Quote condominiali 2024 da giugno sino a settembre»: i mesi ci sono ma '
        + 'il conto non torna, perché giugno-settembre 2024 valgono 65,36 (A8 8,88 + A9 '
        + '7,46 al mese) e non 82,20. Provate tutte le combinazioni delle quote 2024 '
        + 'aperte: nessun sottoinsieme somma 82,20; i valori vicini sono 81,70 e 83,12. '
        + 'Imputarli d’ufficio al debito più vecchio (art. 1193 c.c.) sarebbe una '
        + 'convenzione, non la lettura di un documento: per questo restano qui e non '
        + 'sulle quote. Aperto complessivo su A8 e A9: 4.833,04 dal 2023 al 2026.' },
  { anno: '2025', tipo: 'bilancio', importo: 1135.28,
    titolo: 'Eredi Paratore A22: gennaio-aprile 2025 risultano saldati solo dalla lista dei contanti del portiere',
    nota: 'Le quattro mensilità gennaio-aprile 2025 di A22 (4 × 283,82 = 1.135,28) sono '
        + 'chiuse da incassi in contanti datati 18/12/2025, con nota «incassato dal '
        + 'portiere per contanti e non restituiti» e un «versamento unico di 851,46» che '
        + 'ne copre tre. Non c’è alcun movimento bancario dietro: l’unica fonte è il '
        + 'foglio dei contanti trattenuti, cioè lo stesso documento contestato da cui '
        + 'vengono i 10.108,39. Tutti gli altri mesi di questa posizione, dal 2023 al '
        + '2026, sono invece coperti da bonifici con i mesi dichiarati in causale e '
        + 'l’importo esatto. Il condòmino è liberato comunque, perché ha pagato a chi '
        + 'era incaricato di riscuotere (art. 1188 c.c.), ma verso il condominio quella '
        + 'somma è un credito verso il portiere e non un incasso.' },
  { anno: '2024', tipo: 'bilancio', importo: 1168.14,
    titolo: 'Preventivo 2024: spese 58.015,26 ma rate a carico dei condòmini 56.847,12',
    nota: 'Nel preventivo 2024 (pp. 14-17 del fascicolo 2023) il totale delle spese e la '
        + 'somma delle rate annue differiscono di 1.168,14 dentro lo stesso documento.' },
  // --- dal rendiconto 2024 di Graviano, letto il 22/09/2026 ---------------
  { anno: '2024', tipo: 'bilancio', importo: 14723.66,
    titolo: 'Consuntivo 2024 mai ripartito in rate: gestione senza conguaglio emesso',
    nota: 'Bilancio comparativo del rendiconto 2024: preventivo 58.015,26, consuntivo '
        + '71.570,78, differenza 13.555,52. Le quote 2024 emesse valgono 56.847,12 di '
        + 'gestione ordinaria, quindi 14.723,66 non sono mai stati chiesti. È la stessa '
        + 'cosa già rilevata sul 2023 per 21.729,17.' },
  { anno: '2024', tipo: 'bilancio', importo: 5368.37,
    titolo: 'A11 (70,50 millesimi) addebitata 6,14 al mese: il consuntivo ne chiede 5.442,05',
    nota: 'A11 è fra le unità più grandi e la sua rata mensile registrata è 405,03. Nel '
        + '2024 risultano emessi 73,68 per tutto l’anno. Serie storica, ogni anno da una '
        + 'scansione diversa: 2019 68,65 · 2020 10,90 · 2021 9,31 · 2022 9,09 · 2023 2,25 '
        + '· 2024 6,14 · 2025 e 2026 405,03. Il 72% del conguaglio 2024 della Scala A '
        + '(7.601,35 su 10.559,82) sta sulle quattro unità dove compare S.A.M.O.T. Onlus. '
        + 'Da chiarire con l’amministratore uscente prima di chiedere qualunque '
        + 'conguaglio: potrebbe esistere una delibera, e nessuno di noi lo sa.' },
  { anno: '2024', tipo: 'bilancio', importo: 6000.00,
    titolo: 'Nel registro 2024 non compare nessun pagamento di F24',
    nota: 'Il registro di contabilità del rendiconto 2024 non ha una sola riga di F24, '
        + 'mentre il comparativo mette a costo 6.000,00 di «F24 Irpef, Inail, Inps» e il '
        + 'debito verso l’erario cresce nell’anno di 12.950,93. Nel 2024 gli F24 non sono '
        + 'stati pagati e non è stato scritto che lo fossero; nel 2025 sono stati '
        + 'registrati come pagati senza che un euro uscisse dal conto.' },
  { anno: '2024', tipo: 'bilancio', importo: 2133.87,
    titolo: 'Incassi 2024 dichiarati e non attribuiti in piattaforma',
    nota: 'Il rendiconto dichiara 52.669,02 di versamenti più 6.175,52 di gestione acqua, '
        + 'in tutto 58.844,54; in piattaforma l’incassato 2024 è 56.710,67. Sono 2.133,87 '
        + 'che qualcuno ha versato e che non stanno sulla sua posizione.' },
  { anno: '2026', tipo: 'bilancio', importo: 10450.00,
    titolo: 'Due entrate bancarie per 10.450,00 con causale «ZYR…» non attribuite a nessuno',
    nota: '20/11/2025 per 7.450,00 e 02/04/2026 per 3.000,00, stessa causale '
        + '«ZYR.202513849100-17072025P.2023056280156». Nessuna posizione le rivendica e '
        + 'non si capisce da chi vengano: il codice è un riferimento dell’ordinante e va '
        + 'chiesto alla banca.' },
  { anno: '2026', tipo: 'bilancio', importo: 9149.07,
    titolo: 'S.A.M.O.T. ha versato 9.149,07 più di quanto le sia attribuito',
    nota: 'Fra il 2023 e il 2026 dalla S.A.M.O.T. sono arrivati 39.634,01 in bonifici, e '
        + 'in piattaforma le risultano attribuiti 30.484,94. Va letta insieme all’altra '
        + 'anomalia sulla stessa posizione: A11, sua proprietà con 70,50 millesimi, è '
        + 'stata addebitata di 6,14 al mese nel 2024 e di 2,25 nel 2023. Non è una '
        + 'posizione che non paga: è una posizione i cui addebiti e i cui versamenti non '
        + 'si parlano.' },
  { anno: '2024', tipo: 'bilancio', importo: 29702.72,
    titolo: 'Debiti verso terzi al 31/12/2024 dichiarati dall’amministrazione: 68.127,15',
    nota: 'Importo indicato: la sola quota verso l’erario, per non sommarla ai 38.708,41 '
        + 'di cartelle già in elenco, che sono la posizione di oggi. La tabella è '
        + 'invecchiata per anno: erario 70,80 nel 2020, 743,74 nel 2021, 1.650,38 nel '
        + '2022, poi 14.286,87 nel 2023 e 12.950,93 nel 2024, cioè 27.237,80 su due anni. '
        + 'Nella stessa tabella: AMAP 25.754,00 su tre gestioni acqua, Graviano Pierina '
        + '7.993,44 tutto su 2023 e 2024, M.I.R.E. 2.056,40.' },
  { anno: '2026', tipo: 'bilancio', importo: 8779.62,
    titolo: 'Intimazione AMAP: 8.779,62 ancora dovuti dopo i due bonifici del 2026',
    nota: 'Nota dell’Intercondominio GI.EMME del 04/06/2026: AMAP ha intimato 19.845,00 '
        + 'per bollette emesse al 31/12/2025 (prot. 001-96925-REC/2026, utenza 8285517 '
        + 'DISALIMENTABILE), termine 12/06/2026; la quota di questo condominio è '
        + '17.778,00. Detratti i due bonifici già eseguiti nel 2026 per 8.998,38 '
        + '(5.641,19 il 19/05 e 3.357,19 il 12/06, registrati fra le spese), restano '
        + '8.779,62. La nota è del 04/06 e non tiene conto del bonifico del 12/06: da '
        + 'farsi confermare il nuovo saldo.' },
  { anno: '2026', tipo: 'bilancio', importo: 38708.41,
    titolo: 'Cartelle e avvisi Agenzia entrate-Riscossione: 38.708,41 di debito residuo',
    nota: 'Situazione debitoria al 13/04/2026 per il CF 97057730828, 29 fra cartelle e '
        + 'avvisi: carico affidato 45.123,61, già pagato 8.722,11, residuo carico '
        + '36.401,50 più 2.069,38 di interessi di mora e 237,53 di oneri. Quasi tutti '
        + 'avvisi INPS per contributi del portiere dal 2016 al 2026, più cartelle '
        + 'Agenzia delle Entrate e INAIL. Di questi 5.186,97 sono entrati nella '
        + 'rottamazione-quinquies; i restanti ~33.500 restano dovuti per intero.' },
  { anno: '2026', tipo: 'bilancio', importo: 4366.83,
    titolo: 'Rottamazione-quinquies: 4.366,83 in 43 rate fino al 2033, decadenza dopo due rate saltate',
    nota: 'Adesione del 29/04/2026 per otto cartelle/avvisi. Comunicazione del '
        + '10/06/2026: debito residuo 5.186,97, da pagare in definizione 3.966,65 più '
        + '400,18 di interessi = 4.366,83 in 43 rate bimestrali (42 da 101,25 e '
        + 'l’ultima da 114,33, dal 31/07/2026 al 31/07/2033). Prima rata pagata: '
        + 'residuo 4.265,58 su 42 rate, prossima scadenza 30/09/2026. Con due rate '
        + 'saltate la definizione perde effetto e riprende il recupero coattivo.' },
  { anno: '2026', tipo: 'bilancio', importo: 19902.21,
    titolo: 'Compensi legali chiesti in mediazione: pretesa contestata, non registrata come spesa',
    nota: 'Domanda di mediazione n. 391/2026 (Concilium A.D.R., primo incontro 07/10/2026) '
        + 'degli avv. Graviano e Di Grado: 4.691,07 Di Martino, 1.241,72 United Lion '
        + 'School, 3.379,33 Gallo, 4.440,09 Solarino-Liotta, 6.150,00 per 26 diffide. È '
        + 'l’oggetto contestato del procedimento; registrata solo l’indennità del primo '
        + 'incontro (190,32).' },
  { anno: '2026', tipo: 'bilancio', importo: 3.57,
    titolo: 'Indennizzo assicurativo: il giro chiude, restano 3,57 di commissioni a carico del condominio',
    nota: 'Tre bonifici in uscita con causale «rimborso quota parte versata al '
        + 'condominio in data 20.11.25 da Italiana Assicurazioni, polizza 056280156»: '
        + '7.001,19 e 3.001,19 a Borgia Alessandro (per Torina Luigia) e 451,19 a Torina '
        + 'Rosario, in tutto 10.453,57. L’incasso originario, che mancava, è stato '
        + 'trovato il 22/09 con la riconciliazione quote-banca: sono le due entrate con '
        + 'causale «ZYR.202513849100-17072025P.2023056280156» del 20/11/2025 per 7.450,00 '
        + 'e del 02/04/2026 per 3.000,00, in tutto 10.450,00. Il codice contiene '
        + '056280156, cioè il numero di polizza dei bonifici in uscita. Entrato '
        + '10.450,00, uscito 10.453,57: la differenza di 3,57 sono tre commissioni da '
        + '1,19, ed è l’unica cosa che il condominio ci ha rimesso. Resta da dare '
        + 'all’operazione una scrittura propria: oggi l’entrata non è attribuita e '
        + 'l’uscita sta fra le spese.' },
  { anno: '2026', tipo: 'bilancio', importo: 3790.00,
    titolo: 'Bonifico acqua del 12/06/2026: tre totali diversi sullo stesso pagamento',
    nota: 'La causale elenca otto voci che sommano 3.790,00, il prospetto Galvano del '
        + '04/06/2026 ne indica 3.873,00 e dal conto sono usciti 3.357,19. La spesa è '
        + 'registrata per quanto è uscito dalla banca.' },

  // Trovate il 22/09/2026 misurando le spese registrate anno per anno. Riguardano
  // ciò che nella piattaforma MANCA, e per questo nessun controllo sui movimenti le
  // avrebbe mai fatte emergere: un'assenza non produce righe da esaminare.
  // Fonte: CRUSCOTTO-SPESE-E-CONTANTI-2026-09-22.md.
  { anno: '2023', tipo: 'esercizio_mancante', importo: 0,
    titolo: 'Esercizio 2023: nessuna spesa registrata, l’anno non esiste come gestione',
    nota: 'In piattaforma il 2023 ha zero righe di spesa, mentre gli anni intorno ne '
        + 'hanno centinaia (2022: 82.359,63 su 328 righe; 2025: 128.815,49 su 231). Non '
        + 'significa che non si sia speso: significa che l’esercizio non è mai stato '
        + 'ricostruito dal suo rendiconto. Finché resta così, del 2023 si conoscono le '
        + 'quote emesse e gli incassi, ma non che cosa il condominio abbia pagato — e '
        + 'quindi nessun conguaglio 2023 è verificabile. L’importo è a zero perché la '
        + 'criticità è l’assenza stessa, non una somma.' },
  { anno: '2024', tipo: 'esercizio_mancante', importo: 71433.28,
    titolo: 'Esercizio 2024: registrati 137,50 di spese contro 71.570,78 di consuntivo',
    nota: 'Il rendiconto 2024 di Graviano porta un consuntivo di 71.570,78. In '
        + 'piattaforma il 2024 ha TRE righe di spesa per 137,50 in tutto, tutte sotto '
        + '«ALTRO»: manca il 99,8% dell’esercizio, cioè 71.433,28. È l’anno di cui si '
        + 'discute il conguaglio mai chiesto (14.723,66) e quello in cui non compare un '
        + 'solo pagamento di F24: senza le spese caricate, ogni verifica su quell’anno '
        + 'poggia solo sulle scansioni. Il registro di contabilità (pagine 30-39) è la '
        + 'fonte da cui ricostruirlo.' },
  { anno: '2025', tipo: 'bilancio', importo: 70818.79,
    titolo: 'Esercizio 2025: più di metà della spesa è caricata sotto «ALTRO»',
    nota: 'Il 2025 ha 128.815,49 di spese su 231 righe, ma 70.818,79 — il 55% — stanno '
        + 'sotto la voce «ALTRO», cioè non sono classificate. Le righe ci sono e i '
        + 'totali tornano; quello che non si può dire è quanto sia costato il '
        + 'portierato, quanto l’ascensore, quanto i legali. Finché la voce resta quella, '
        + 'il confronto fra esercizi e il riparto per tabella non si possono fare.' }
];

const eur = (v) => Number(v || 0).toLocaleString('it-IT',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* Se qualcosa qui dentro esplode, non deve portarsi via la Dashboard: questa
   sezione vive dentro una pagina che si usa tutti i giorni. */
class Recinto extends Component {
  constructor(p) { super(p); this.state = { rotto: false }; }
  static getDerivedStateFromError() { return { rotto: true }; }
  render() {
    if (this.state.rotto) {
      return (
        <Card><CardContent className="pt-5 text-sm text-muted-foreground">
          Il cruscotto non è riuscito a disegnarsi. Il resto della pagina è a posto.
        </CardContent></Card>
      );
    }
    return this.props.children;
  }
}

/* Barre raggruppate in SVG. Niente librerie: `recharts` non è installato e
   aggiungere una dipendenza per tre grafici allungherebbe build e rischio. */
const Barre = ({ serie, chiavi, annoAttivo }) => {
  const anni = Object.keys(serie).sort();
  if (!anni.length) return <p className="text-sm text-muted-foreground">Nessun dato.</p>;
  const massimo = Math.max(1, ...anni.flatMap((a) => chiavi.map((k) => serie[a][k] || 0)));
  const L = 820, H = 300, ml = 66, mb = 34, mt = 14;
  const pa = (L - ml - 12) / anni.length;
  const pb = Math.min(28, (pa - 14) / chiavi.length);
  const y = (v) => mt + (H - mt - mb) * (1 - v / massimo);

  return (
    <svg viewBox={`0 0 ${L} ${H}`} className="w-full h-auto" role="img">
      {[0, 1, 2, 3, 4].map((i) => {
        const v = (massimo * i) / 4;
        return (
          <g key={i}>
            <line x1={ml} y1={y(v)} x2={L - 12} y2={y(v)} stroke="#E8E3DA" strokeWidth="1" />
            <text x={ml - 8} y={y(v) + 4} fontSize="10" fill="#6B6357" textAnchor="end">{eur(v)}</text>
          </g>
        );
      })}
      {anni.map((a, i) => {
        const x0 = ml + i * pa;
        const spento = annoAttivo !== 'tutti' && annoAttivo !== a;
        return (
          <g key={a} opacity={spento ? 0.16 : 1} style={{ transition: 'opacity .15s' }}>
            {chiavi.map((k, j) => {
              const v = serie[a][k] || 0;
              const yy = y(v);
              return (
                <rect key={k} x={x0 + (pa - pb * chiavi.length) / 2 + j * pb} y={yy}
                      width={Math.max(pb - 3, 1)} height={Math.max(H - mb - yy, 0)}
                      fill={COLORI[k] || '#888'}>
                  <title>{`${a} ${k}: € ${eur(v)}`}</title>
                </rect>
              );
            })}
            <text x={x0 + pa / 2} y={H - 12} fontSize="12" fill="#2B2B2B" textAnchor="middle">{a}</text>
          </g>
        );
      })}
    </svg>
  );
};

const Legenda = ({ chiavi }) => (
  <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
    {chiavi.map((k) => (
      <span key={k} className="flex items-center gap-1.5">
        <i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COLORI[k] }} />
        {k}
      </span>
    ))}
  </div>
);

const Contenuto = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id || selectedCondominium?.id;
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);
  const [anno, setAnno] = useState('tutti');
  /* 🔑 I due assi degli incassi si SCELGONO, non si decidono qui.
     «cassa» = l'anno in cui il denaro è entrato (payment_date): risponde a «quanto
     contante è passato dallo sportello quell'anno».
     «competenza» = l'anno della quota: risponde a «con che cosa è stato pagato
     l'esercizio 2023».
     Sono due verità diverse sullo stesso denaro e la differenza non è piccola: sul
     2023 la competenza mostra 555,58 di contanti e 2.045,74 di POS, la cassa zero di
     entrambi, perché quelle somme sono state incassate nel 2024 e nel 2025.
     Fissare un solo asse fa sparire dalla pagina numeri veri, e chi guarda non ha modo
     di sapere che sono altrove: sembrano persi. */
  /* 🔴 Il valore predefinito è «competenza», cioè quello che la pagina mostrava PRIMA
     che io la toccassi. Avevo messo «cassa» perché contabilmente risponde meglio alla
     domanda sul contante allo sportello, e il risultato è stato che sul 2023 contanti
     e POS sono spariti dal grafico — 555,58 e 2.045,74 che c'erano e non c'erano più.
     Erano andati sull'anno in cui quel denaro è entrato, ma chi guardava la pagina non
     aveva modo di saperlo: vedeva solo che un dato era sparito.
     La regola che ne esce: quando si aggiunge un modo nuovo di guardare gli stessi
     dati, il predefinito resta quello vecchio. Il nuovo si offre, non si impone —
     altrimenti non è un'aggiunta, è una sottrazione con un bottone accanto. */
  const [asseIncassi, setAsseIncassi] = useState('competenza');

  const carica = useCallback(async () => {
    if (!condoId) { setLoading(false); return; }
    setLoading(true); setErrore(null);
    const h = { Authorization: `Bearer ${token}` };
    try {
      const rp = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods`, { headers: h });
      if (!rp.ok) throw new Error('periodi');
      const periodi = (await rp.json())?.periods || [];

      const anni = {};
      const tocca = (a) => (anni[a] = anni[a] || {
        emesso: 0, incassato: 0, residuo: 0, contanti: 0, pos: 0, bonifico: 0, altro: 0
      });

      /* 🔑 Gli incassi per strumento stanno su DUE assi che non coincidono, e
         confonderli è ciò che rendeva il contante illeggibile. L'anno della QUOTA è
         la competenza: dice a quale esercizio quel denaro appartiene. L'anno del
         PAGAMENTO è la cassa: dice quando il denaro è davvero entrato. Su questo
         condominio i due divergono di parecchio, perché nel 2025 sono stati incassati
         arretrati di anni prima: a competenza il 2026 risulta con zero contanti,
         a cassa ne ha 308,68. Il grafico degli strumenti usa l'asse di CASSA, che è
         quello che risponde alla domanda «quanto contante è passato dallo sportello
         quell'anno»; emesso e incassato restano invece a competenza, che è il loro
         asse proprio. */
      const cassa = {};
      const toccaCassa = (a) => (cassa[a] = cassa[a] || {
        contanti: 0, pos: 0, bonifico: 0, altro: 0
      });

      /* Le quote si chiedono un periodo alla volta: sono una cinquantina di
         richieste. Si mandano a gruppi di sei per non aprirne cinquanta insieme. */
      for (let i = 0; i < periodi.length; i += 6) {
        const lotto = periodi.slice(i, i + 6);
        const risposte = await Promise.all(lotto.map((p) =>
          fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods/${p.id}/quotes`, { headers: h })
            .then((r) => (r.ok ? r.json() : { quotes: [] })).catch(() => ({ quotes: [] }))));
        risposte.forEach((res, j) => {
          const a = tocca(String(lotto[j].year));
          (res?.quotes || []).forEach((q) => {
            a.emesso += Number(q.total_amount || 0);
            a.incassato += Number(q.paid_amount || 0);
            (q.payments || []).forEach((pg) => {
              const m = String(pg.payment_method || '').toUpperCase();
              const k = m.includes('CONTANT') ? 'contanti'
                : m.includes('POS') ? 'pos'
                : m.includes('BONIFIC') ? 'bonifico' : 'altro';
              const imp = Number(pg.amount || 0);
              a[k] += imp;
              const ap = String(pg.payment_date || '').slice(0, 4);
              if (ap) toccaCassa(ap)[k] += imp;
            });
          });
        });
      }
      Object.values(anni).forEach((a) => { a.residuo = a.emesso - a.incassato; });

      const banca = {};
      const mov = [];
      let skip = 0;
      for (let giro = 0; giro < 12; giro++) {
        const rb = await fetch(
          `${API_URL}/api/condominiums/${condoId}/bank-transactions?limit=500&skip=${skip}`,
          { headers: h });
        const lotto = rb.ok ? ((await rb.json())?.items || []) : [];
        lotto.forEach((m) => {
          const a = String(m.transaction_date || '').slice(0, 4);
          if (!a) return;
          banca[a] = banca[a] || { entrate: 0, uscite: 0 };
          const v = Number(m.amount || 0);
          if (v > 0) banca[a].entrate += v; else banca[a].uscite += -v;
          mov.push({ anno: a, importo: v, d: String(m.description || '') });
        });
        if (lotto.length < 500) break;
        skip += 500;
      }

      /* Il saldo di fine anno: l'apertura della serie più tutti i netti fino a
         quell'anno compreso. Va costruito in ordine di anno, non per anno isolato. */
      const saldo = {};
      let progressivo = APERTURA_SERIE;
      Object.keys(banca).sort().forEach((a) => {
        progressivo += banca[a].entrate - banca[a].uscite;
        saldo[a] = progressivo;
      });

      /* Le spese si chiedono UN ANNO ALLA VOLTA. `GET /expenses` tronca a 1000 righe
         e ignora `limit` e `skip` (difetto D26): chiederle tutte insieme ne perde
         centinaia in silenzio, e l'anno più vecchio sparisce per primo. `from_date` e
         `to_date` invece li rispetta. L'importo sta in `total`: un `amount` qui non
         solleva errori, restituisce zero, e l'anno sembra a costo zero. */
      const spese = {};
      const categorie = {};
      const oggi = new Date().getFullYear();
      for (let a = 2018; a <= oggi; a += 1) {
        const re = await fetch(
          `${API_URL}/api/condominiums/${condoId}/expenses`
          + `?from_date=${a}-01-01&to_date=${a}-12-31`, { headers: h });
        if (!re.ok) continue;
        const corpo = await re.json();
        const righe = Array.isArray(corpo) ? corpo : (corpo?.items || corpo?.expenses || []);
        const k = String(a);
        /* 🔑 L'anno senza spese prende comunque la sua casella, a zero. Saltandolo, il
           2023 SPARIVA dal grafico: l'asse passava da 2022 a 2024 e l'anno più
           problematico dell'archivio era l'unico che non si vedeva. Un anno assente e
           un anno a zero raccontano la stessa cosa — che quell'esercizio non è stato
           ricostruito — ma solo il secondo la racconta a chi guarda. */
        spese[k] = { spese: 0, pagate: 0, righe: righe.length };
        categorie[k] = {};
        if (!righe.length) continue;
        righe.forEach((e) => {
          const v = Number(e.total || 0);
          spese[k].spese += v;
          if (String(e.status || '').toUpperCase() === 'PAID') spese[k].pagate += v;
          const c = String(e.category || 'SENZA CATEGORIA');
          categorie[k][c] = (categorie[k][c] || 0) + v;
        });
      }

      // le criticita' che stanno in banca si ricavano dai movimenti, ogni volta
      const cr = {};
      const aggiungi = (a, c) => { (cr[a] = cr[a] || []).push(c); };
      mov.forEach((m) => {
        const abs = Math.abs(m.importo);
        if (RE_770.test(m.d)) {
          aggiungi(m.anno, { tipo: 'settecentosettanta', importo: abs,
            titolo: 'Compenso 770 / consulenza del lavoro', nota: m.d.slice(0, 190) });
        }
        if (RE_PARCELLA.test(m.d) && abs >= 1000) {
          aggiungi(m.anno, { tipo: 'parcella', importo: abs,
            titolo: 'Parcella dell’amministrazione', nota: m.d.slice(0, 190) });
        }
      });
      Object.keys(POLIZZA_ANNI).forEach((a) => {
        const [trovate, importo, dovute] = POLIZZA_ANNI[a];
        const dovuto = Math.round(dovute * RATA_POLIZZA * 100) / 100;
        const diff = trovate - dovute;
        const conto = diff < 0
          ? `Ne mancano ${-diff}, per € ${eur(Math.round((dovuto - importo) * 100) / 100)}.`
          : diff > 0
            ? `Ne risultano ${diff} in più del dovuto annuo: sono arretrati dell’anno `
              + 'precedente pagati in ritardo, come mostrano le quietanze del documento '
              + 'Unipol, saldate con uno o due mesi di scarto.'
            : 'Il conto dell’anno torna esatto.';
        aggiungi(a, { tipo: 'polizza_rate', importo: dovuto,
          titolo: `Polizza Catalano: ${dovute} rate dovute per € ${eur(dovuto)} `
                + `(rata mensile € ${eur(RATA_POLIZZA)})`,
          nota: 'La polizza (Unipol, contratto 589/100778864, contraente CATALANO '
              + 'SALVATORE, decorrenza 15/03/2012) prevede dodici quietanze mensili da '
              + `105,00: € ${eur(dovuto)} di costo annuo per il condominio`
              + (a === '2025' ? ' (nel 2025 solo gennaio-luglio, fino al riscatto)' : '')
              + `. In banca risultano ${trovate} addebiti per € ${eur(importo)}, quasi `
              + `tutti «ADDEBITO ASSEGNO» senza causale. ${conto} Le rate che in assegno `
              + 'non compaiono sono state pagate per bonifico o in contanti nello stesso '
              + 'anno: vanno cercate lì, non sono rate non pagate.' });
      });
      FUORI_BANCA.forEach((c) => aggiungi(c.anno, { ...c, fuori_banca: true }));
      DOCUMENTALI.forEach((c) => aggiungi(c.anno, { ...c, documentale: true }));

      setDati({ anni, cassa, banca, saldo, spese, categorie,
                criticita: cr, movimenti: mov.length });
    } catch (e) {
      setErrore('Non sono riuscito a leggere i dati: riprova con «Ricarica».');
      setDati(null);
    } finally {
      setLoading(false);
    }
  }, [condoId, token]);

  useEffect(() => { carica(); }, [carica]);

  const anniDisponibili = useMemo(() => dati ? Array.from(new Set([
    ...Object.keys(dati.anni), ...Object.keys(dati.cassa), ...Object.keys(dati.banca),
    ...Object.keys(dati.spese), ...Object.keys(dati.criticita)
  ])).sort() : [], [dati]);

  const schede = useMemo(() => {
    if (!dati) return [];
    const out = [];
    Object.keys(dati.criticita).sort().forEach((a) => {
      if (anno !== 'tutti' && a !== anno) return;
      dati.criticita[a]
        .slice().sort((x, z) => z.importo - x.importo)
        .forEach((c, i) => out.push({ ...c, anno: a, key: `${a}-${i}` }));
    });
    return out;
  }, [dati, anno]);

  /* 🔑 Le schede in alto seguono il filtro come tutto il resto. Prima sommavano
     SEMPRE tutti gli anni: si sceglieva il 2024 e restavano i 260.197,24 di sette
     esercizi, cioè il filtro spegneva le barre dei grafici e lasciava in testa alla
     pagina quattro numeri che non c'entravano con l'anno scelto. Sono i numeri che
     si leggono per primi, quindi era il posto peggiore dove lasciarli sbagliati.

     Nel totale «fuori dal conto» entrano solo le somme davvero uscite senza passare
     dal conto: le voci marcate `non_sommare` restano fra le criticità ma sono un
     mancato guadagno, non un esborso. */
  const dentro = (a) => anno === 'tutti' || a === anno;

  const fuoriBanca = FUORI_BANCA.reduce(
    (s, c) => (c.non_sommare || !dentro(c.anno) ? s : s + c.importo), 0);

  const tot = Object.entries(dati?.anni || {}).reduce((s, [a, v]) => (dentro(a) ? {
    emesso: s.emesso + v.emesso, incassato: s.incassato + v.incassato
  } : s), { emesso: 0, incassato: 0 });

  const speseFiltrate = Object.entries(dati?.spese || {}).reduce(
    (s, [a, v]) => (dentro(a) ? s + v.spese : s), 0);

  /* Il saldo non si somma: è una fotografia. Su un anno è il saldo al 31 dicembre di
     quell'anno; su «tutti gli anni» è l'ultimo disponibile, cioè il saldo di oggi.
     Sommare i saldi di fine anno darebbe un numero che non significa niente. */
  const anniSaldo = Object.keys(dati?.saldo || {}).sort();
  const annoSaldo = anno === 'tutti' ? anniSaldo[anniSaldo.length - 1]
    : (dati?.saldo?.[anno] !== undefined ? anno : null);
  const saldoMostrato = annoSaldo ? dati.saldo[annoSaldo] : null;
  const primoAnno = anniSaldo[0];
  const ultimoAnno = anniSaldo[anniSaldo.length - 1];

  /* 🔑 Quando la scheda non ha un numero da mostrare deve dire PERCHÉ, e i perché non
     sono lo stesso. Un trattino muto con scritto «nessun movimento per questo anno»
     copre due casi opposti: l'anno è fuori dalla serie (il 2018 c'è fra le spese ma i
     movimenti bancari cominciano nel 2019, e allora il vuoto è il dato giusto), oppure
     i movimenti non sono arrivati affatto (e allora è un guasto, e va detto). Sono i
     due zeri che non vanno confusi: «non c'è niente» e «non ho potuto guardare». */
  const perchePieno = (() => {
    if (saldoMostrato !== null) return null;
    if (!anniSaldo.length) {
      return `nessun movimento bancario letto (${dati?.movimenti || 0} in archivio): `
           + 'riprova con «Ricarica»';
    }
    if (anno !== 'tutti' && anno < primoAnno) {
      return `la serie dei movimenti comincia nel ${primoAnno}`;
    }
    return `nessun movimento nel ${anno}, fra il ${primoAnno} e il ${ultimoAnno}`;
  })();

  if (!condoId) {
    return <p className="text-sm text-muted-foreground">Seleziona prima un condominio.</p>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Cruscotto</h2>
          <p className="text-sm text-muted-foreground">
            Andamento per anno e criticit&agrave; riscontrate. I grafici si ricalcolano
            a ogni apertura sui dati della piattaforma.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={carica} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Ricarica
        </Button>
      </div>

      {errore && <p className="text-sm text-red-700">{errore}</p>}
      {loading && <p className="text-sm text-muted-foreground">Lettura dei dati in corso&hellip;</p>}

      {!loading && dati && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: 'Emesso', v: tot.emesso, i: TrendingUp,
                s: anno === 'tutti' ? 'tutti gli esercizi' : `esercizio ${anno}` },
              { t: 'Incassato', v: tot.incassato, i: Banknote,
                s: tot.emesso
                  ? `${(100 * tot.incassato / tot.emesso).toFixed(1)}% dell'emesso` : null },
              { t: 'Residuo', v: tot.emesso - tot.incassato, i: AlertTriangle,
                s: 'quote emesse e non ancora incassate' },
              /* L'anno in corso non ha un 31 dicembre: per l'ultimo anno della serie
                 il saldo è quello all'ultimo movimento in archivio, e dirlo «al
                 31/12» sarebbe una data che non è ancora arrivata. */
              { t: 'Saldo del conto', v: saldoMostrato, i: Landmark,
                s: saldoMostrato === null ? perchePieno
                  : annoSaldo === ultimoAnno
                    ? `all'ultimo movimento in archivio (${ultimoAnno})`
                    : `al 31/12/${annoSaldo}` },
              /* `!dati.spese[anno]` non basta più: adesso l'anno vuoto la sua casella
                 ce l'ha, con `righe` a zero. La domanda giusta è quante righe ha, non
                 se la casella esiste. */
              { t: 'Spese registrate', v: speseFiltrate, i: TrendingUp,
                s: anno !== 'tutti' && !(dati.spese[anno]?.righe)
                  ? 'nessuna spesa registrata per questo anno'
                  : `${anno === 'tutti'
                      ? Object.values(dati.spese).reduce((s, v) => s + v.righe, 0)
                      : dati.spese[anno].righe} righe` },
              { t: 'Fuori dal conto', v: fuoriBanca, i: Landmark,
                s: 'somme documentate mai transitate in banca' }
            ].map((k) => (
              <Card key={k.t}>
                <CardContent className="pt-5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                    <k.i className="w-3.5 h-3.5" />{k.t}
                  </div>
                  <div className="text-xl font-semibold mt-1">
                    {k.v === null ? '—' : <>&euro; {eur(k.v)}</>}
                  </div>
                  {k.s && <div className="text-xs text-muted-foreground mt-0.5">{k.s}</div>}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {['tutti', ...anniDisponibili].map((a) => (
              <Button key={a} size="sm" variant={anno === a ? 'default' : 'outline'}
                      onClick={() => setAnno(a)}>
                {a === 'tutti' ? 'tutti gli anni' : a}
              </Button>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Incassi per strumento di pagamento</CardTitle>
              <CardDescription>
                Il contante &egrave; quello che passa dallo sportello del portiere.
                {asseIncassi === 'cassa'
                  ? <> L&rsquo;anno qui &egrave; <b>quello in cui il denaro &egrave;
                      entrato</b>. Le quote di un esercizio pagate in ritardo compaiono
                      sull&rsquo;anno del pagamento, non su quello della quota.</>
                  : <> L&rsquo;anno qui &egrave; <b>quello della quota</b>, cio&egrave;
                      l&rsquo;esercizio a cui il denaro appartiene, anche se &egrave;
                      entrato dopo.</>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-3">
                {[['cassa', 'quando è entrato'],
                  ['competenza', 'a quale esercizio appartiene']].map(([k, et]) => (
                    <Button key={k} size="sm"
                            variant={asseIncassi === k ? 'default' : 'outline'}
                            onClick={() => setAsseIncassi(k)}>
                      {et}
                    </Button>
                  ))}
              </div>
              <Barre serie={asseIncassi === 'cassa' ? dati.cassa : dati.anni}
                     chiavi={['contanti', 'pos', 'bonifico', 'altro']} annoAttivo={anno} />
              <Legenda chiavi={['contanti', 'pos', 'bonifico', 'altro']} />
              {anno !== 'tutti' && dati.cassa[anno] && dati.anni[anno] && (
                <p className="text-xs text-muted-foreground mt-3">
                  Nel {anno}: per cassa &euro; {eur(['contanti', 'pos', 'bonifico', 'altro']
                    .reduce((s, k) => s + (dati.cassa[anno][k] || 0), 0))}, per competenza
                  &euro; {eur(['contanti', 'pos', 'bonifico', 'altro']
                    .reduce((s, k) => s + (dati.anni[anno][k] || 0), 0))}. La differenza
                  non &egrave; denaro perso: &egrave; denaro incassato in un anno diverso
                  da quello della quota che paga.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Emesso, incassato e insoluto</CardTitle>
              <CardDescription>
                Il 2023 &egrave; in corso di rettifica: il suo insoluto reale &egrave;
                superiore a quello mostrato.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Barre serie={dati.anni} chiavi={['emesso', 'incassato', 'residuo']} annoAttivo={anno} />
              <Legenda chiavi={['emesso', 'incassato', 'residuo']} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Banca: entrate e uscite</CardTitle>
              <CardDescription>
                Dai movimenti importati: {dati.movimenti} in archivio.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Barre serie={dati.banca} chiavi={['entrate', 'uscite']} annoAttivo={anno} />
              <Legenda chiavi={['entrate', 'uscite']} />
              <div className="mt-4 text-sm">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
                  Saldo a fine anno
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1">
                  {Object.keys(dati.saldo).sort().map((a) => (
                    <span key={a} className={anno !== 'tutti' && anno !== a
                      ? 'opacity-40' : 'font-medium'}>
                      {a} &euro; {eur(dati.saldo[a])}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Il saldo parte da &euro; {eur(APERTURA_SERIE)}, che il conto aveva
                  gi&agrave; il {APERTURA_DA}, quando comincia la serie dei movimenti
                  in archivio. Senza quell&rsquo;apertura i soli movimenti darebbero un
                  saldo negativo, che il conto non ha mai avuto. Il numero si ricava
                  per sottrazione da due saldi stampati sugli estratti conto e i due
                  calcoli coincidono, il che prova anche che fra quelle date la serie
                  &egrave; completa.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Spese registrate per anno</CardTitle>
              <CardDescription>
                Dalle spese caricate in piattaforma, lette un anno alla volta. Dove un
                anno &egrave; basso o assente <b>non significa che si sia speso poco</b>:
                significa che quell&rsquo;esercizio non &egrave; ancora stato
                ricostruito dai rendiconti.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Barre serie={dati.spese} chiavi={['spese', 'pagate']} annoAttivo={anno} />
              <Legenda chiavi={['spese', 'pagate']} />
              {anno !== 'tutti' && Object.keys(dati.categorie[anno] || {}).length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
                    Voci di spesa del {anno}
                  </div>
                  <div className="space-y-1">
                    {Object.entries(dati.categorie[anno])
                      .sort((x, z) => z[1] - x[1])
                      .map(([c, v]) => (
                        <div key={c} className="flex justify-between text-sm gap-4">
                          <span className="text-muted-foreground">{c}</span>
                          <span className="font-medium whitespace-nowrap">&euro; {eur(v)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              {anno !== 'tutti' && !Object.keys(dati.categorie[anno] || {}).length && (
                <p className="text-sm mt-4" style={{ color: '#8B1E2D' }}>
                  <b>Per il {anno} non risulta registrata nessuna spesa.</b> Non vuol
                  dire che non se ne siano fatte: vuol dire che quell&rsquo;esercizio
                  non &egrave; ancora stato ricostruito dai rendiconti.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Criticit&agrave; riscontrate</CardTitle>
              <CardDescription>
                Quelle che stanno in banca si ricavano dai movimenti a ogni apertura.
                Le schede con <b>mai transitata sul conto</b> sono somme documentate che
                nell&rsquo;estratto conto non compaiono e non si possono calcolare:
                valgono &euro; {eur(fuoriBanca)} in tutto. La scheda segnata
                <b> mancato guadagno</b> &egrave; la perdita sul riscatto della polizza:
                resta fra le criticit&agrave; ma non si somma, perch&eacute; nessun
                denaro &egrave; uscito dal conto. Le schede con
                <b> contraddizione fra documenti</b> sono discrepanze fra riparto,
                elenco insolventi, preventivo e piattaforma: non si sommano.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2.5">
              {schede.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nessuna criticit&agrave; per questo anno.
                </p>
              )}
              {schede.map((c) => (
                <div key={c.key} className="border rounded-md p-3.5"
                     style={{ borderLeft: '3px solid #8B1E2D' }}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="secondary">{ETICHETTE[c.tipo] || c.tipo}</Badge>
                    <span className="font-medium">{c.anno}</span>
                    {c.fuori_banca && !c.non_sommare && (
                      <Badge variant="outline" className="text-red-700 border-red-300">
                        mai transitata sul conto
                      </Badge>
                    )}
                    {c.non_sommare && (
                      <Badge variant="outline" className="text-red-700 border-red-300">
                        mancato guadagno, non sommato
                      </Badge>
                    )}
                    {c.documentale && (
                      <Badge variant="outline" className="text-amber-800 border-amber-300">
                        contraddizione fra documenti
                      </Badge>
                    )}
                  </div>
                  <div className="text-lg font-semibold">&euro; {eur(c.importo)}</div>
                  <p className="text-sm text-muted-foreground mt-1">{c.titolo}</p>
                  <p className="text-xs text-muted-foreground mt-1">{c.nota}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export const CruscottoSezione = () => (
  <Recinto><Contenuto /></Recinto>
);

export const Cruscotto = () => (
  <div className="p-6 max-w-6xl">
    <Recinto><Contenuto /></Recinto>
  </div>
);

export default Cruscotto;
